import { z } from 'zod/v4';

// StoredFile schema for UI
export const storedFileSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  size: z.number(),
  url: z.string(),
  createdAt: z.date()
});

// Exercise type enum
export const exerciseTypeEnum = z.enum(['MultipleChoice', 'Written', 'Recorded', 'FillInBlank', 'SelectForBlank', 'Conversational', 'Matching', 'Scramble', 'FileUpload']);

// Base exercise schema
export const baseExerciseSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  order: z.number().optional(),
  type: exerciseTypeEnum,
  correction: z.string() // Guidance for how to evaluate submissions
});

const multipleChoiceOptionSchema = z.object({
  optionId: z.string().optional(),
  label: z.string(),
  isCorrect: z.boolean().optional().default(false),
  order: z.number().optional()
}).passthrough();

// Multiple choice exercise schema
export const multipleChoiceExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.MultipleChoice),
  question: z.string(),
  options: z.array(multipleChoiceOptionSchema).min(2),
  images: z.array(storedFileSchema).optional()
});

// Written exercise schema
export const writtenExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.Written),
  expectedWordCount: z.number().optional(),
  images: z.array(storedFileSchema).optional()
});

// Recorded exercise schema
export const recordedExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.Recorded),
  videos: z.array(storedFileSchema).optional(),
  audios: z.array(storedFileSchema).optional(),
  images: z.array(storedFileSchema).optional()
});

const fillInBlankBlankSchema = z.object({
  id: z.string(),
  position: z.number().int().nonnegative(),
  answer: z.string(),
  correction: z.string().optional()
});

const fillInBlankSentenceSchema = z.object({
  id: z.string(),
  sentence: z.string(),
  order: z.number().optional(),
  correction: z.string().optional(),
  blanks: z.array(fillInBlankBlankSchema).min(1)
});

export const fillInBlankExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.FillInBlank),
  sentences: z.array(fillInBlankSentenceSchema).min(1),
  images: z.array(storedFileSchema).optional()
});

const selectForBlankOptionSchema = z.object({
  id: z.string(),
  label: z.string(),
  isCorrect: z.boolean().default(false),
  order: z.number().optional()
});

const selectForBlankBlankSchema = z.object({
  id: z.string(),
  position: z.number().int().nonnegative(),
  options: z.array(selectForBlankOptionSchema).min(1)
});

const selectForBlankSentenceSchema = z.object({
  id: z.string(),
  sentence: z.string(),
  order: z.number().optional(),
  correction: z.string().optional(),
  blanks: z.array(selectForBlankBlankSchema).min(1)
});

export const selectForBlankExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.SelectForBlank),
  sentences: z.array(selectForBlankSentenceSchema).min(1),
  images: z.array(storedFileSchema).optional()
});

// Conversational exercise schema - for real-time AI voice conversations
export const conversationalExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.Conversational),
  theme: z.string(), // e.g., "ordering food at a restaurant"
  maxDurationSec: z.number().int().positive().default(300),
  systemPrompt: z.string().optional(), // Custom AI behavior instructions
  targetSkills: z.array(z.string()).optional(), // Skills to develop during the conversation
});

// Matching exercise schema - connect prompts with their correct options via drag and drop
const matchingPairSchema = z.object({
  pairId: z.string(),
  promptLabel: z.string(), // Left column item (stem)
  optionLabel: z.string(), // Right column item (match)
  order: z.number().optional()
});

export const matchingExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.Matching),
  pairs: z.array(matchingPairSchema).min(2),
  images: z.array(storedFileSchema).optional()
});

// Scramble exercise schema - unscramble words to form sentences
const scrambleSentenceSchema = z.object({
  id: z.string(),
  correctSentence: z.string(), // The correct sentence (words will be split by spaces)
  order: z.number().optional(),
  correction: z.string().optional()
});

export const scrambleExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.Scramble),
  sentences: z.array(scrambleSentenceSchema).min(1),
  images: z.array(storedFileSchema).optional()
});

// File upload exercise schema - students upload files (PDF, video, PPT) as submissions
export const fileUploadExerciseSchema = baseExerciseSchema.extend({
  type: z.literal(exerciseTypeEnum.enum.FileUpload),
  images: z.array(storedFileSchema).optional()
});

// Union type for all exercise types
export const exerciseSchema = z.discriminatedUnion('type', [
  multipleChoiceExerciseSchema,
  writtenExerciseSchema,
  recordedExerciseSchema,
  fillInBlankExerciseSchema,
  selectForBlankExerciseSchema,
  conversationalExerciseSchema,
  matchingExerciseSchema,
  scrambleExerciseSchema,
  fileUploadExerciseSchema
]);

// Function to create a new exercise of specified type (internal)
export const createNewExerciseFromSchema = (type: z.infer<typeof exerciseTypeEnum>, order: number = 0) => {
  const id = `exercise_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  const name = `New ${type} Exercise`;

  switch (type) {
    case 'MultipleChoice':
      return multipleChoiceExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        question: "",
        options: [
          { optionId: `${id}_option_0`, label: "", isCorrect: true, order: 0 },
          { optionId: `${id}_option_1`, label: "", isCorrect: false, order: 1 },
        ],
        images: []
      });
    case 'Written':
      return writtenExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        expectedWordCount: 250,
        images: []
      });
    case 'Recorded':
      return recordedExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        videos: [],
        audios: [],
        images: []
      });
    case 'FillInBlank':
      return fillInBlankExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        sentences: [
          {
            id: `${id}_sentence_0`,
            sentence: "___",
            order: 0,
            correction: "",
            blanks: [
              {
                id: `${id}_sentence_0_blank_0`,
                position: 0,
                answer: "",
                correction: ""
              }
            ]
          }
        ],
        images: []
      });
    case 'SelectForBlank':
      return selectForBlankExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        sentences: [
          {
            id: `${id}_sentence_0`,
            sentence: "___",
            order: 0,
            correction: "",
            blanks: [
              {
                id: `${id}_sentence_0_blank_0`,
                position: 0,
                options: [
                  { id: `${id}_sentence_0_blank_0_option_0`, label: "", isCorrect: true, order: 0 },
                  { id: `${id}_sentence_0_blank_0_option_1`, label: "", isCorrect: false, order: 1 }
                ]
              }
            ]
          }
        ],
        images: []
      });
    case 'Conversational':
      return conversationalExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        theme: "",
        maxDurationSec: 300,
        systemPrompt: "",
        targetSkills: []
      });
    case 'Matching':
      return matchingExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        pairs: [
          { pairId: `${id}_pair_0`, promptLabel: "", optionLabel: "", order: 0 },
          { pairId: `${id}_pair_1`, promptLabel: "", optionLabel: "", order: 1 }
        ],
        images: []
      });
    case 'Scramble':
      return scrambleExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        sentences: [
          {
            id: `${id}_sentence_0`,
            correctSentence: "",
            order: 0,
            correction: ""
          }
        ],
        images: []
      });
    case 'FileUpload':
      return fileUploadExerciseSchema.parse({
        id,
        name,
        type,
        order,
        correction: "",
        images: []
      });
  }
};
